import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, IconButton, Link, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { RootState } from '../../store';
import { PomBannerService } from '../../services/pomBanner.service';
import { PomBannerTypes } from '../../types/pomBanner.types';
import { GT_COLORS } from '../../constants/designTokens.constants';

const BANNER_STYLES: Record<
    PomBannerTypes.BannerType,
    { bg: string; border: string; text: string }
> = {
    info: {
        bg: GT_COLORS.primaryLight,
        border: GT_COLORS.primary,
        text: GT_COLORS.primary,
    },
    success: {
        bg: GT_COLORS.greenBg,
        border: GT_COLORS.greenBorder,
        text: GT_COLORS.green,
    },
    warning: {
        bg: GT_COLORS.amberBg,
        border: GT_COLORS.amberBorder,
        text: GT_COLORS.amber,
    },
    error: {
        bg: GT_COLORS.redBg,
        border: GT_COLORS.redBorder,
        text: GT_COLORS.red,
    },
};

const BANNER_ICONS: Record<PomBannerTypes.BannerType, React.ReactNode> = {
    info: <InfoOutlinedIcon fontSize="small" />,
    success: <CheckCircleOutlineIcon fontSize="small" />,
    warning: <WarningAmberIcon fontSize="small" />,
    error: <ErrorOutlineIcon fontSize="small" />,
};

export const BannerDisplay: React.FC = () => {
    const authUser = useSelector((state: RootState) => state.auth.authUser);
    const [banners, setBanners] = useState<PomBannerTypes.Model[]>([]);
    const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

    const isPomMaster = authUser.pomMaster && !authUser.masquarade;

    useEffect(() => {
        if (isPomMaster) return;

        let ignore = false;

        PomBannerService.fetchActive().then((result) => {
            if (!ignore) {
                setBanners(result);
            }
        });

        return () => {
            ignore = true;
        };
    }, [isPomMaster]);

    if (isPomMaster) return null;

    const visibleBanners = banners.filter((b) => !hiddenIds.has(b.id));

    if (visibleBanners.length === 0) return null;

    const handleClose = (id: string) => {
        setHiddenIds((prev) => new Set(prev).add(id));
    };

    const handleDismiss = async (id: string) => {
        setHiddenIds((prev) => new Set(prev).add(id));
        await PomBannerService.dismiss(id);
    };

    return (
        <Stack
            spacing='8px'
            sx={{
                position: 'sticky',
                top: { xs: 60, lg: 60 },
                zIndex: 1099,
                padding: '12px 24px',
                mt: -5,
                mb: 5,
            }}
        >
            {visibleBanners.map((banner) => {
                const styles = BANNER_STYLES[banner.type];

                return (
                    <Box
                        key={banner.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px 16px',
                            backgroundColor: styles.bg,
                            borderLeft: `4px solid ${styles.border}`,
                            borderRadius: '8px',
                        }}
                    >
                        <Box sx={{ color: styles.text, display: 'flex', flexShrink: 0 }}>
                            {BANNER_ICONS[banner.type]}
                        </Box>

                        <Typography
                            variant="body2"
                            sx={{
                                color: styles.text,
                                flex: 1,
                                lineHeight: 1.5,
                            }}
                        >
                            {banner.message}
                            {banner.linkText && banner.linkUrl && /^https?:\/\//i.test(banner.linkUrl) && (
                                <>
                                    {' - '}
                                    <Link
                                        href={banner.linkUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            color: styles.text,
                                            fontWeight: 600,
                                            textDecorationColor: 'inherit',
                                        }}
                                    >
                                        {banner.linkText}
                                    </Link>
                                </>
                            )}
                        </Typography>

                        {banner.allowDontShowAgain && (
                            <Button
                                size="small"
                                onClick={() => handleDismiss(banner.id)}
                                sx={{
                                    color: styles.text,
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    fontSize: '12px',
                                }}
                            >
                                Don't show again
                            </Button>
                        )}

                        <IconButton
                            size="small"
                            onClick={() => handleClose(banner.id)}
                            sx={{ color: styles.text, flexShrink: 0 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                );
            })}
        </Stack>
    );
};
